<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <v-app>
    <!-- Top Navigation Bar -->
    <v-app-bar :color="colors.primaryDark" dark elevation="4" height="60">
      <div class="d-flex align-center">
        <v-icon size="40" class="ml-7 mr-2 pulse">fas fa-building</v-icon>
        <span class="text-h6 font-weight-bold gradient-text">
          SICON PAINEL DE CONTROLE I.A
        </span>
      </div>
      <v-spacer></v-spacer>
      <div class="d-flex align-center" style="gap: 24px">
        <!-- User Profile -->
        <div class="d-flex align-center mr-12 user-profile" style="gap: 7px">
          <v-avatar size="40" :color="colors.primaryLight" class="profile-avatar">
            <v-icon color="white" size="20">fas fa-user</v-icon>
          </v-avatar>
          <div class="d-flex flex-column">
            <span class="font-weight-bold text-body-2">
              {{ userDetails?.name || 'Carregando...' }}
            </span>
            <span class="text-caption opacity-75">
              {{ userDetails?.department?.name || 'Online' }}
            </span>
          </div>
        </div>
      </div>
    </v-app-bar>

    <!-- Sidebar Navigation -->
    <v-navigation-drawer
      v-model="drawer"
      :color="colors.primaryDark"
      dark
      permanent
      width="250"
      class="custom-drawer"
    >
      <!-- User Profile in Sidebar -->
      <div class="pa-4 sidebar-profile" style="border-bottom: 1px solid #1a4b7a">
        <div class="d-flex align-center" style="gap: 12px">
          <v-avatar size="45" :color="colors.primaryLight" class="profile-avatar">
            <v-icon color="white" size="22">fas fa-user</v-icon>
          </v-avatar>
          <div>
            <div class="font-weight-bold text-body-2 lowercase">
              {{ userDetails?.name || 'Carregando...' }}
            </div>
            <div class="text-caption opacity-75">
              {{ userDetails?.department?.name || 'Sistema Online' }}
            </div>
          </div>
        </div>
      </div>

      <!-- Navigation Menu -->
      <v-list nav class="pa-0 text-start">
        <v-list-item
          v-for="item in menuItems"
          :key="item.title"
          :value="item.value"
          :active="item.active"
          rounded="xl"
          class="mb-2 menu-item"
          :class="item.active ? 'active-item' : ''"
          lines="one"
          density="comfortable"
          @click="router.push(item.route)"
        >
          <template #prepend>
            <v-icon size="22" class="mr-2">{{ item.icon }}</v-icon>
          </template>

          <template #title>
            <span class="item-title">{{ item.title }}</span>
          </template>

          <template #append v-if="item.badge">
            <v-chip size="small" :color="item.badgeColor" class="pulse-badge">
              {{ item.badge }}
            </v-chip>
          </template>
        </v-list-item>
      </v-list>

      <template v-slot:append>
        <div class="pa-3" style="border-top: 1px solid #1a4b7a">
          <v-btn
            block
            variant="outlined"
            prepend-icon="fas fa-sign-out-alt"
            class="logout-btn"
            @click="logout"
          >
            Sair
          </v-btn>
        </div>
      </template>
    </v-navigation-drawer>

    <!-- Main Content -->
    <v-main class="bg-grey-lighten-5 main-content" style="margin-top: 20px">
      <!-- Chat Container -->
      <div class="chat-container">
        <!-- Chat Header -->
        <div class="chat-header">
          <div class="d-flex align-center" style="gap: 12px">
            <v-avatar :color="colors.primaryLight" size="40">
              <v-icon color="white" size="20">fas fa-robot</v-icon>
            </v-avatar>
            <div>
              <div class="font-weight-bold text-h6">Assistente IA SICON</div>
              <div class="text-caption" :style="{ color: colors.primaryLight }">
                <v-icon size="8" :color="colors.primaryGreen" class="mr-1">fas fa-circle</v-icon>
                Online e pronto para ajudar
              </div>
            </div>
          </div>
          <v-btn
            icon
            variant="text"
            size="small"
            @click="clearChat"
          >
            <v-icon>fas fa-trash</v-icon>
          </v-btn>
        </div>

        <!-- Chat Messages -->
        <div class="chat-messages" ref="messagesContainer">
          <div v-if="messages.length === 0" class="empty-chat">
            <v-icon size="64" :color="colors.primaryLight" class="mb-4">fas fa-comments</v-icon>
            <div class="text-h6 mb-2">Bem-vindo ao Chat IA!</div>
            <div class="text-body-2 text-grey-darken-1">
              Envie uma mensagem, imagem ou documento para começar a conversa
            </div>
          </div>

          <div
            v-for="(message, index) in messages"
            :key="index"
            class="message-wrapper"
            :class="message.sender === 'user' ? 'user-message' : 'ai-message'"
          >
            <div class="message-bubble" :class="message.sender === 'user' ? 'user-bubble' : 'ai-bubble'">
              <div v-if="message.type === 'text'" class="message-content">
                {{ message.content }}
              </div>
              <div v-else-if="message.type === 'image'" class="message-image">
                <v-img
                  :src="message.content"
                  max-width="300"
                  max-height="200"
                  class="rounded-lg"
                  cover
                  @click="openImageModal(message.content)"
                />
              </div>
              <div class="message-time">
                {{ formatTime(message.timestamp) }}
              </div>
            </div>
          </div>

          <!-- Typing Indicator -->
          <div v-if="isTyping" class="message-wrapper ai-message">
            <div class="loading-container">
              <div class="loading-card">
                <div class="loading-icon">
                  <v-icon size="32" :color="colors.primaryLight">fas fa-robot</v-icon>
                </div>
                <div class="loading-text">
                  <div class="font-weight-bold mb-1">Assistente IA está pensando...</div>
                  <div class="text-caption text-grey">
                    Processando sua solicitação, isso pode levar alguns segundos
                  </div>
                </div>
                <div class="loading-dots-container">
                  <div class="loading-dot"></div>
                  <div class="loading-dot"></div>
                  <div class="loading-dot"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Chat Input -->
        <div class="chat-input">
          <div class="input-container">
            <v-text-field
              v-model="newMessage"
              placeholder="Digite sua mensagem..."
              variant="outlined"
              density="compact"
              hide-details
              class="message-input"
              @keypress.enter="sendMessage()"
              :disabled="isTyping"
            >
              <template #prepend-inner>
                <v-btn
                  icon
                  variant="text"
                  size="small"
                  @click="triggerDocumentUpload"
                  :disabled="isTyping"
                  title="Enviar documento (PDF, DOCX, Excel, CSV)"
                >
                  <v-icon>fas fa-paperclip</v-icon>
                </v-btn>
                <v-btn
                  icon
                  variant="text"
                  size="small"
                  @click="triggerImageUpload"
                  :disabled="isTyping"
                  title="Enviar imagem"
                >
                  <v-icon>fas fa-image</v-icon>
                </v-btn>
              </template>
              <template #append-inner>
                <v-btn
                  icon
                  variant="text"
                  size="small"
                  :color="colors.primaryLight"
                  @click="sendMessage()"
                  :disabled="!newMessage.trim() || isTyping"
                >
                  <v-icon>fas fa-paper-plane</v-icon>
                </v-btn>
              </template>
            </v-text-field>
          </div>
        </div>
      </div>

      <!-- Hidden File Inputs -->
      <input
        ref="imageInput"
        type="file"
        accept="image/*"
        style="display: none"
        @change="handleImageUpload"
      />
      <input
        ref="documentInput"
        type="file"
        accept=".pdf,.docx,.doc,.xlsx,.xls,.csv"
        multiple
        style="display: none"
        @change="handleDocumentUpload"
      />

      <!-- Image Modal -->
      <v-dialog v-model="imageModal" max-width="800">
        <v-card>
          <v-card-text class="pa-0">
            <v-img :src="selectedImage" contain max-height="600" />
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn @click="imageModal = false">Fechar</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-main>
  </v-app>
</template>

<script setup>
import { ref, onMounted, nextTick } from "vue";
import { useRouter } from 'vue-router';
import axios from "axios";
import { useAuthStore } from '@/stores/auth'
import { menuItems } from '@/constants/MenuItems';

// Environment variables
const API_BASE = (process.env.VUE_APP_API_URL || '').replace(/\/$/, '');
const hostIA = `${API_BASE}/back-ia`;

// State
const drawer = ref(true);
const userDetails = ref({
  name: "João Silva",
  department: { name: "Desenvolvimento" }
});

const router = useRouter();
const authStore = useAuthStore();
const auth = useAuthStore();
const token = auth?.token || null;
const messages = ref([]);
const newMessage = ref("");
const isTyping = ref(false);
const messagesContainer = ref(null);
const imageInput = ref(null);
const documentInput = ref(null);
const imageModal = ref(false);
const selectedImage = ref("");
const isMounted = ref(false);
const sessionId = ref(null);
const threadId = ref(null);

const fetchUserDetails = async () => {
    if (!isMounted.value || !token) return;

    try {
      const response = await axios.get(`${API_BASE}/me`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });

      if (isMounted.value && response.data) {
        userDetails.value = response.data;
      }
    } catch (error) {
      console.error('Erro ao buscar dados do usuário:', error);
      if (isMounted.value) {
        userDetails.value = {
          name: "Usuário",
          department: { name: "Sistema" }
        };
      }
    }
}

const sendMessage = async (files = null) => {
  if ((!newMessage.value.trim() && !files) || isTyping.value) return;

  const messageText = newMessage.value.trim();
  
  // Adiciona a mensagem do usuário imediatamente
  const userMessage = {
    sender: "user",
    type: files ? "file" : "text",
    content: messageText || "Arquivo enviado",
    timestamp: new Date()
  };

  messages.value.push(userMessage);
  newMessage.value = "";

  // Scroll para baixo imediatamente após adicionar a mensagem do usuário
  await nextTick();
  await scrollToBottom();
  
  // Ativa o indicador de digitação ANTES da chamada da API
  isTyping.value = true;
  
  try {
    const formData = new FormData();
    formData.append('question', messageText);
    formData.append('user_id', userDetails.value.id || 'default-user');
    
    if (sessionId.value) {
      formData.append('session_id', sessionId.value);
    }
    
    if (files && files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
      }
    }

    const response = await axios.post(
      `${hostIA}/internal-assistant`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': token ? `Bearer ${token}` : undefined,
          'x-api-key': '1'
        },
        timeout: 60000 // Timeout de 60 segundos
      }
    );

    // Verifica se a resposta foi bem-sucedida
    if (!response.data.success) {
      throw new Error(response.data.error || "Erro ao processar a mensagem");
    }

    // Armazena session_id e thread_id para próximas mensagens
    if (response.data.session_id) {
      sessionId.value = response.data.session_id;
    }

    if (response.data.thread_id) {
      threadId.value = response.data.thread_id;
    }

    // Adiciona a resposta da IA
    const aiMessage = {
      sender: "ai",
      type: "text",
      content: response.data.response,
      timestamp: new Date()
    };

    messages.value.push(aiMessage);
    
  } catch (error) {
    console.error('Erro ao enviar mensagem:', error);
    
    let errorText = "Desculpe, ocorreu um erro ao processar sua mensagem.";
    
    if (error.code === 'ECONNABORTED') {
      errorText = "A solicitação demorou muito tempo. Por favor, tente novamente.";
    } else if (error.response) {
      errorText = `Erro no servidor: ${error.response.status}. Tente novamente.`;
    } else if (error.request) {
      errorText = "Não foi possível conectar ao servidor. Verifique sua conexão.";
    }
    
    const errorMessage = {
      sender: "ai",
      type: "text",
      content: errorText,
      timestamp: new Date()
    };
    
    messages.value.push(errorMessage);
  } finally {
    isTyping.value = false;
    await nextTick();
    await scrollToBottom();
  }
};

const triggerImageUpload = () => {
  imageInput.value.click();
};

const triggerDocumentUpload = () => {
  documentInput.value.click();
};

const handleImageUpload = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onload = async (e) => {
    const imageMessage = {
      sender: "user",
      type: "image",
      content: e.target.result,
      timestamp: new Date()
    };

    messages.value.push(imageMessage);
    await scrollToBottom();

    // Enviar imagem para a API
    await sendMessage([file]);
  };

  reader.readAsDataURL(file);
  event.target.value = "";
};

const handleDocumentUpload = async (event) => {
  const files = Array.from(event.target.files);
  if (files.length === 0) return;

  const fileNames = files.map(f => f.name).join(', ');
  
  const documentMessage = {
    sender: "user",
    type: "text",
    content: `📎 Documento(s) enviado(s): ${fileNames}`,
    timestamp: new Date()
  };

  messages.value.push(documentMessage);
  await scrollToBottom();

  // Enviar documentos para a API
  await sendMessage(files);
  
  event.target.value = "";
};

const openImageModal = (imageSrc) => {
  selectedImage.value = imageSrc;
  imageModal.value = true;
};

const clearChat = () => {
  messages.value = [];
  sessionId.value = null;
  threadId.value = null;
};

const scrollToBottom = async () => {
  await nextTick();
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
  }
};

const formatTime = (timestamp) => {
  return new Date(timestamp).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit'
  });
};

const logout = () => {
  try {
    if (authStore && typeof authStore.logout === 'function') {
      authStore.logout();
    }
    if (router && typeof router.push === 'function') {
      router.push('/login').catch(() => {
        router.push('/').catch(() => {
          window.location.href = '/';
        });
      });
    } else {
      window.location.href = '/';
    }
  } catch (error) {
    console.error('Erro ao fazer logout:', error);
    window.location.href = '/';
  }
};

// Lifecycle
onMounted(async() => {
    isMounted.value = true;
    await fetchUserDetails();
    
    if (userDetails.value?.name) {
      const welcomeMessage = {
        sender: "ai",
        type: "text",
        content: `Olá ${userDetails.value.name}! Bem-vindo ao Chat IA da SICON. Como posso ajudá-lo hoje?`,
        timestamp: new Date()
      };
      messages.value.push(welcomeMessage);
    }
});

// Colors
const colors = {
  primaryDark: "#123A5D",
  primaryLight: "#57C2E3",
  primaryGreen: "#A3C940",
  lightBg: "#E7F2F9",
};
</script>

<style scoped>
/* Animations */
@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
}

@keyframes typing {
  0%, 60%, 100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-10px);
  }
}

@keyframes loadingPulse {
  0%, 100% {
    opacity: 0.4;
    transform: scale(0.8);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Component Styles */
.gradient-text {
  background: linear-gradient(45deg, #57C2E3, #A3C940);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.pulse {
  animation: pulse 2s infinite;
}

.user-profile {
  transition: all 0.3s ease;
  border-radius: 12px;
  padding: 8px 12px;
}

.user-profile:hover {
  background: rgba(255, 255, 255, 0.1);
  cursor: pointer;
}

.profile-avatar {
  border: 2px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
}

.profile-avatar:hover {
  border-color: rgba(255, 255, 255, 0.6);
  transform: scale(1.05);
}

.custom-drawer {
  background: linear-gradient(180deg, #123A5D 0%, #0f2a42 100%);
}

.menu-item {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin: 4px 0;
  position: relative;
  overflow: hidden;
  cursor: pointer;
}

.menu-item:hover {
  background: rgba(255, 255, 255, 0.12) !important;
  transform: translateX(4px);
}

.active-item {
  background: linear-gradient(135deg, #57C2E3, #4aa3c1) !important;
  box-shadow: 0 4px 12px rgba(87, 194, 227, 0.3);
}

.active-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 4px;
  background: #A3C940;
}

.pulse-badge {
  animation: pulse 2s infinite;
}

.logout-btn {
  border-color: rgba(255, 255, 255, 0.3);
  color: rgba(255, 255, 255, 0.8);
  transition: all 0.3s ease;
}

.logout-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.6);
}

.main-content {
  background: linear-gradient(135deg, #f5f5f5 0%, #e8f4f8 100%);
}

.lowercase {
  text-transform: lowercase;
}

/* Chat Styles */
.chat-container {
  height: calc(100vh - 140px);
  max-width: 1200px;
  margin: 0 auto;
  background: white;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chat-header {
  padding: 20px 24px;
  border-bottom: 1px solid #e0e0e0;
  background: linear-gradient(135deg, #123A5D 0%, #0f2a42 100%);
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background: #f8fafc;
}

.chat-messages::-webkit-scrollbar {
  width: 8px;
}

.chat-messages::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.chat-messages::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.chat-messages::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.empty-chat {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
  opacity: 0.6;
}

.message-wrapper {
  display: flex;
  margin-bottom: 16px;
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.user-message {
  justify-content: flex-end;
}

.ai-message {
  justify-content: flex-start;
}

.message-bubble {
  max-width: 70%;
  padding: 12px 16px;
  border-radius: 18px;
  position: relative;
  word-wrap: break-word;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.user-bubble {
  background: linear-gradient(135deg, #57C2E3, #4aa3c1);
  color: white !important;
  border-bottom-right-radius: 4px;
  box-shadow: 0 4px 12px rgba(87, 194, 227, 0.3);
}

.user-bubble .message-content {
  color: rgb(0, 0, 0) !important;
}

.user-bubble .message-time {
  color: rgba(255, 255, 255, 0.9) !important;
}

.ai-bubble {
  background: white;
  color: #333;
  border: 1px solid #e0e0e0;
  border-bottom-left-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.message-content {
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

.message-image img {
  border-radius: 12px;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.message-image img:hover {
  transform: scale(1.02);
}

.message-time {
  font-size: 11px;
  opacity: 0.7;
  margin-top: 6px;
  text-align: right;
}

/* Loading Container */
.loading-container {
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 20px 0;
  animation: slideIn 0.4s ease-out;
}

.loading-card {
  background: white;
  border-radius: 16px;
  padding: 24px 32px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  min-width: 320px;
  border: 2px solid #57C2E3;
}

.loading-icon {
  animation: loadingPulse 2s infinite;
}

.loading-text {
  text-align: center;
}

.loading-dots-container {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}

.loading-dot {
  width: 10px;
  height: 10px;
  background: #57C2E3;
  border-radius: 50%;
  animation: loadingPulse 1.4s infinite;
}

.loading-dot:nth-child(2) {
  animation-delay: 0.2s;
}

.loading-dot:nth-child(3) {
  animation-delay: 0.4s;
}

.typing-indicator {
  padding: 16px 20px;
}

.typing-dots {
  display: flex;
  gap: 6px;
  align-items: center;
}

.typing-dots span {
  width: 8px;
  height: 8px;
  background: #57C2E3;
  border-radius: 50%;
  animation: typing 1.4s infinite;
}

.typing-dots span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-dots span:nth-child(3) {
  animation-delay: 0.4s;
}

.chat-input {
  padding: 20px 24px;
  background: white;
  border-top: 1px solid #e0e0e0;
}

.input-container {
  width: 100%;
  max-width: 100%;
}

.message-input {
  border-radius: 24px !important;
}

.message-input :deep(.v-field) {
  border-radius: 24px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.message-input :deep(.v-field--focused) {
  box-shadow: 0 4px 12px rgba(87, 194, 227, 0.2);
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .user-profile span:last-child {
    display: none;
  }
  
  .message-bubble {
    max-width: 85%;
  }
  
  .chat-container {
    margin: 0 16px;
    height: calc(100vh - 160px);
  }
}

@media (max-width: 600px) {
  .gradient-text {
    font-size: 1rem;
  }
  
  .chat-messages {
    padding: 16px;
  }
  
  .chat-input {
    padding: 16px;
  }
  
  .message-bubble {
    max-width: 90%;
  }
}
</style>
